import React, { useState } from 'react';
import { Button, Icon, Input } from '@tviklax/ui-components';
import { formatNumber } from '@tviklax/ui-components';
import {
  ItemsCard,
  ItemsHeader,
  ItemsTitle,
  ItemsTitleRow,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  EditableSelect,
  ActionButtons,
  MobileItemCard,
  MobileItemRow,
  MobileItemLabel,
  MobileItemValue,
  RowMetaBar,
  CompactEmpty,
  AdvancedRow,
  AdvancedMeta,
  AdvancedLabel,
  AdvancedValue,
} from './styles';
import { OrderItem } from './types';

interface ItemsSectionProps {
  items: OrderItem[];
  editingIndex: number | null;
  onEdit: (index: number) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: (index: number) => void;
  onFieldChange: (index: number, field: keyof OrderItem, value: string | number) => void;
  onAddItem: () => void;
}

export const ItemsSection: React.FC<ItemsSectionProps> = ({
  items,
  editingIndex,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onFieldChange,
  onAddItem,
}) => {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [columnWidths, setColumnWidths] = useState({
    product: 220,
    hsCode: 150,
    category: 160,
    unitPrice: 140,
  });

  const handleMouseDown = (column: keyof typeof columnWidths, e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[column];

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.max(100, startWidth + (moveEvent.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [column]: newWidth }));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <ItemsCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
      <ItemsHeader>
        <ItemsTitleRow>
          <ItemsTitle>Items & Commercials</ItemsTitle>
          <RowMetaBar>
            <span>{items.length} item{items.length === 1 ? '' : 's'}</span>
          </RowMetaBar>
        </ItemsTitleRow>
        <Button variant="default" size="sm" onClick={onAddItem}>
          <Icon name="Plus" size={16} style={{ marginRight: '8px' }} />
          Add Item
        </Button>
      </ItemsHeader>

      {/* Mobile Card View (Mandatory) */}
      <div className="mobile-only">
        {items.map((row, index) => (
          <MobileItemCard key={`mobile-${index}`}>
            <MobileItemRow>
              <MobileItemLabel>Item # {row.itemno}</MobileItemLabel>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="ghost" size="sm" onClick={() => onEdit(index)}><Icon name="Pencil" size={14} /></Button>
                <Button variant="ghost" size="sm" onClick={() => onDelete(index)} style={{ color: '#ef4444' }}><Icon name="Trash2" size={14} /></Button>
              </div>
            </MobileItemRow>
            <div style={{ paddingBottom: '8px', borderBottom: '1px solid #f1f5f9', marginBottom: '8px' }}>
              <div style={{ fontWeight: 600, color: '#1e293b' }}>{row.productName || row.details}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>HS: {row.hsCode || '—'} | {row.category || 'Uncategorized'}</div>
            </div>
            <MobileItemRow>
              <MobileItemLabel>Unit | Qty</MobileItemLabel>
              <MobileItemValue>{row.unit} | {row.quantity}</MobileItemValue>
            </MobileItemRow>
            <MobileItemRow>
              <MobileItemLabel>Unit Price | Total</MobileItemLabel>
              <MobileItemValue>{formatNumber(row.price || row.unitPrice)} | <span style={{ color: '#f86400' }}>{formatNumber(row.total || row.subtotal)}</span></MobileItemValue>
            </MobileItemRow>
          </MobileItemCard>
        ))}
        {items.length === 0 && (
          <CompactEmpty>
            <div>No items added yet.</div>
          </CompactEmpty>
        )}
      </div>

      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <TableHeader style={{ width: '60px' }}>#</TableHeader>
              <TableHeader style={{ width: `${columnWidths.product}px`, cursor: 'col-resize', position: 'relative', userSelect: 'none' }} onMouseDown={(e) => handleMouseDown('product', e)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Product
                  <span style={{ width: '4px', height: '16px', background: '#cbd5e1', cursor: 'col-resize', marginLeft: '4px' }} />
                </div>
              </TableHeader>
              <TableHeader style={{ width: `${columnWidths.hsCode}px`, cursor: 'col-resize', position: 'relative', userSelect: 'none' }} onMouseDown={(e) => handleMouseDown('hsCode', e)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  HS Code
                  <span style={{ width: '4px', height: '16px', background: '#cbd5e1', cursor: 'col-resize', marginLeft: '4px' }} />
                </div>
              </TableHeader>
              <TableHeader style={{ width: `${columnWidths.category}px`, cursor: 'col-resize', position: 'relative', userSelect: 'none' }} onMouseDown={(e) => handleMouseDown('category', e)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Category
                  <span style={{ width: '4px', height: '16px', background: '#cbd5e1', cursor: 'col-resize', marginLeft: '4px' }} />
                </div>
              </TableHeader>
              <TableHeader style={{ width: '110px', minWidth: '100px' }}>Qty</TableHeader>
              <TableHeader style={{ width: '120px', minWidth: '110px' }}>Unit</TableHeader>
              <TableHeader style={{ width: `${columnWidths.unitPrice}px`, cursor: 'col-resize', position: 'relative', userSelect: 'none' }} onMouseDown={(e) => handleMouseDown('unitPrice', e)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Unit Price
                  <span style={{ width: '4px', height: '16px', background: '#cbd5e1', cursor: 'col-resize', marginLeft: '4px' }} />
                </div>
              </TableHeader>
              <TableHeader style={{ width: '120px' }}>Total</TableHeader>
              <TableHeader style={{ width: '140px' }}>Actions</TableHeader>
            </tr>
          </TableHead>
          <TableBody>
            {items.map((row, index) => {
              const isEditing = editingIndex === index;

              return (
                <React.Fragment key={`${row.itemno}-${index}`}>
                  <TableRow>
                    <TableCell>{row.itemno}</TableCell>
                    <TableCell style={{ width: `${columnWidths.product}px` }}>
                      {isEditing ? (
                        <Input
                          value={row.productName || row.details}
                          onChange={(e) => onFieldChange(index, 'productName', e.target.value)}
                          placeholder="Product Name"
                          size="sm"
                        />
                      ) : (
                        <div style={{ fontWeight: 500 }}>{row.productName || row.details}</div>
                      )}
                    </TableCell>
                    <TableCell style={{ width: `${columnWidths.hsCode}px` }}>
                      {isEditing ? (
                        <Input
                          value={row.hsCode || ''}
                          onChange={(e) => onFieldChange(index, 'hsCode', e.target.value)}
                          placeholder="HS Code"
                          size="sm"
                        />
                      ) : (
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{row.hsCode || '—'}</span>
                      )}
                    </TableCell>
                    <TableCell style={{ width: `${columnWidths.category}px` }}>
                      {isEditing ? (
                        <Input
                          value={row.category || ''}
                          onChange={(e) => onFieldChange(index, 'category', e.target.value)}
                          placeholder="Category"
                          size="sm"
                        />
                      ) : (
                        row.category || '—'
                      )}
                    </TableCell>
                    <TableCell style={{ width: '110px', minWidth: '100px' }}>
                      {isEditing ? (
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          step="any"
                          value={row.quantity}
                          onChange={(e) => onFieldChange(index, 'quantity', e.target.value)}
                          size="sm"
                        />
                      ) : (
                        row.quantity
                      )}
                    </TableCell>
                    <TableCell style={{ width: '120px', minWidth: '110px' }}>
                      {isEditing ? (
                        <EditableSelect
                          value={row.unit || ''}
                          onChange={(e) => onFieldChange(index, 'unit', e.target.value)}
                        >
                          <option value="pcs">pcs</option>
                          <option value="kg">kg</option>
                          <option value="carton">ctn</option>
                        </EditableSelect>
                      ) : (
                        row.unit
                      )}
                    </TableCell>
                    <TableCell style={{ width: `${columnWidths.unitPrice}px` }}>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={row.price || row.unitPrice}
                          onChange={(e) => onFieldChange(index, 'price', e.target.value)}
                          size="sm"
                        />
                      ) : (
                        formatNumber(row.price || row.unitPrice)
                      )}
                    </TableCell>
                    <TableCell style={{ fontWeight: 600, color: '#f86400' }}>
                      {formatNumber(row.total || row.subtotal)}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <ActionButtons>
                          <Button variant="ghost" size="sm" onClick={onSave}><Icon name="CheckCircle" size={16} color="#10b981" /></Button>
                          <Button variant="ghost" size="sm" onClick={onCancel}><Icon name="XCircle" size={16} color="#ef4444" /></Button>
                        </ActionButtons>
                      ) : (
                        <ActionButtons>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View duties / risk"
                            onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                          >
                            <Icon name="AlertCircle" size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => onEdit(index)}><Icon name="Pencil" size={16} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => onDelete(index)}><Icon name="Trash2" size={16} color="#ef4444" /></Button>
                        </ActionButtons>
                      )}
                    </TableCell>
                  </TableRow>

                  {expandedRow === index && (
                    <AdvancedRow>
                      <TableCell colSpan={9}>
                        <AdvancedMeta>
                          <div>
                            <AdvancedLabel>Country of Origin</AdvancedLabel>
                            <AdvancedValue>{row.countryOfOrigin || '—'}</AdvancedValue>
                          </div>
                          <div>
                            <AdvancedLabel>HSN validation</AdvancedLabel>
                            <AdvancedValue>{row.hsCode ? 'Validated' : 'Pending HS code'}</AdvancedValue>
                          </div>
                          <div>
                            <AdvancedLabel>Export control</AdvancedLabel>
                            <AdvancedValue>{row.exportControlFlag || 'Not flagged'}</AdvancedValue>
                          </div>
                          <div>
                            <AdvancedLabel>ECGC eligibility</AdvancedLabel>
                            <AdvancedValue>{row.ecgcEligibility || 'Pending check'}</AdvancedValue>
                          </div>
                        </AdvancedMeta>
                      </TableCell>
                    </AdvancedRow>
                  )}
                </React.Fragment>
              );
            })}

            {/* inline addition row removed in favor of header action for cleaner UI */}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} style={{ textAlign: 'center', color: '#64748b', height: '80px' }}>
                    No items added yet.
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>
    </ItemsCard>
  );
};
